/**
 * 首屏分数输入卡（Hero 区下沿 -mt 上压）
 *
 * 设计目标：
 *  - 首屏看起来就是一个**完整可用**的测算工具，不要预先告诉用户"还没开始"——保住权威感
 *  - 用户输入 + 点击后，若当年公告未发布，弹一个友好的确认对话框：
 *      「2026 中考已结束，成绩和录取公告尚未发布，将用 2025 数据为你做一次预演」
 *      → 用户点确认才进入
 *  - 公告发布后自动切换为直接进入测算，无需任何代码改动
 */
import { useState } from 'react'
import { useApp } from '@/store/useApp'
import type { LifeStage, StageInfo } from '@/domain/stage'
import { track } from '@/lib/track'

export function ScoreInputCard({ stageInfo }: { stageInfo: StageInfo }) {
  const { year, setScore, setWizardStep } = useApp()
  const [value, setValue] = useState<string>('')
  const [shake, setShake] = useState(false)
  const [pendingScore, setPendingScore] = useState<number | null>(null)

  // 「预演态」判定：当年分段表未录入 → 用的就是去年数据，需要弹确认
  const REHEARSAL_STAGES = new Set(['pre_exam', 'near_exam', 'in_exam', 'wait_score'])
  const isRehearsal =
    REHEARSAL_STAGES.has(stageInfo.stage) ||
    (stageInfo.stage === 'after_score' && !stageInfo.canCalculate)

  function enterWizard(score: number, rehearsal: boolean) {
    track('drill_click', {
      target: 'open_wizard',
      from: 'hero_score_input',
      score,
      year,
      rehearsal
    })
    setScore(score)
    setWizardStep('intent')
  }

  const handleSubmit = () => {
    const n = parseInt(value, 10)
    if (!Number.isFinite(n) || n < 200 || n > 750) {
      setShake(true)
      setTimeout(() => setShake(false), 400)
      return
    }
    // 预演态 → 弹确认对话框
    if (isRehearsal) {
      setPendingScore(n)
      return
    }
    enterWizard(n, false)
  }

  return (
    <>
      <div
        className={`
          bg-white rounded-2xl shadow-card border border-ink-100
          p-4
          ${shake ? 'animate-[shake_0.4s_ease-in-out]' : ''}
        `}
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-1.5">
            <span className="inline-block w-1.5 h-1.5 rounded-full bg-green-500" />
            <span className="text-ink-700 text-[12px] font-medium">立即测算</span>
          </div>
          <span className="text-ink-400 text-[11px]">邹城五校 · 概率区间</span>
        </div>

        <div className="flex items-stretch gap-2">
          <div className="flex-1 relative">
            <input
              type="number"
              inputMode="numeric"
              pattern="[0-9]*"
              min={100}
              max={750}
              value={value}
              onChange={(e) => setValue(e.target.value.replace(/[^\d]/g, '').slice(0, 3))}
              onKeyDown={(e) => {
                if (e.key === 'Enter') handleSubmit()
              }}
              placeholder="输入分数"
              className="
                w-full h-12 px-4
                text-h2 font-medium text-ink-900
                tabular-nums tracking-tight
                bg-paper-50 border border-ink-200 rounded-xl
                focus:outline-none focus:ring-2 focus:ring-brand-400 focus:border-brand-400
                placeholder:text-ink-300 placeholder:font-normal placeholder:text-base
              "
            />
            <span className="absolute right-4 top-1/2 -translate-y-1/2 text-ink-400 text-sm">
              分
            </span>
          </div>

          <button
            onClick={handleSubmit}
            className="
              shrink-0 px-5 h-12 rounded-xl
              bg-brand-800 text-white font-medium
              shadow-sm
              active:bg-brand-900 active:scale-[0.98]
              transition
              flex items-center gap-1.5
            "
          >
            <span>开始测算</span>
            <span>→</span>
          </button>
        </div>

        <div className="mt-3 flex items-center justify-between text-[11px] text-ink-400">
          <span>3 秒出概率区间 + 「冲/稳/保」建议</span>
          <span>数据本机计算</span>
        </div>
      </div>

      {/* 预演确认对话框 —— 点击后才出现，不在首屏暴露 */}
      {pendingScore !== null && (
        <RehearsalConfirm
          score={pendingScore}
          stage={stageInfo.stage}
          onCancel={() => setPendingScore(null)}
          onConfirm={() => {
            const s = pendingScore
            setPendingScore(null)
            enterWizard(s, true)
          }}
        />
      )}
    </>
  )
}

function RehearsalConfirm({
  score,
  stage,
  onCancel,
  onConfirm
}: {
  score: number
  stage: LifeStage
  onCancel: () => void
  onConfirm: () => void
}) {
  const copy = getRehearsalCopy(stage)

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-ink-900/60 backdrop-blur-sm"
      onClick={onCancel}
    >
      <div
        className="
          w-full max-w-mobile bg-white rounded-t-2xl sm:rounded-2xl shadow-2xl
          p-5 mx-0 sm:mx-4
          animate-[shake_0.0s]
        "
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3 mb-4">
          <div className="shrink-0 w-10 h-10 rounded-full bg-accent-50 flex items-center justify-center text-xl">
            ⏳
          </div>
          <div className="flex-1">
            <div className="text-ink-900 text-h3 font-medium leading-snug mb-1">
              {copy.title}
            </div>
            <p className="text-ink-600 text-sm leading-relaxed">
              我们用最近一年（2025）的真实录取数据，
              <span className="text-ink-800 font-medium">为你的 {score} 分</span>
              {copy.bodySuffix}
            </p>
          </div>
        </div>

        <div className="rounded-xl bg-paper-50 border border-ink-100 px-3.5 py-3 mb-4 text-[12px] text-ink-600 leading-relaxed">
          <div className="flex items-center gap-1.5 mb-1">
            <span className="text-accent-700">✦</span>
            <span className="text-ink-700 font-medium">预演的价值</span>
          </div>
          <ul className="space-y-0.5 pl-4 list-disc marker:text-ink-300">
            {copy.bullets.map((text) => (
              <li key={text}>{text}</li>
            ))}
          </ul>
        </div>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            className="flex-1 h-11 rounded-xl border border-ink-200 text-ink-700 active:bg-ink-50 transition"
          >
            再等等
          </button>
          <button
            onClick={onConfirm}
            className="
              flex-[1.4] h-11 rounded-xl bg-brand-800 text-white font-medium
              active:bg-brand-900 active:scale-[0.98] transition
              flex items-center justify-center gap-1.5
            "
          >
            <span>开始预演</span>
            <span>→</span>
          </button>
        </div>
      </div>
    </div>
  )
}

function getRehearsalCopy(stage: LifeStage): {
  title: string
  bodySuffix: string
  bullets: string[]
} {
  if (stage === 'in_exam') {
    return {
      title: '2026 中考正在进行中',
      bodySuffix: '先做一次预演。考试结束、成绩和录取公告发布后，我们会尽快切换为 2026 版正式测算。',
      bullets: [
        '考试期间先了解五校之间的分数梯度',
        '提前看清「冲 / 稳 / 保」三档区间',
        '出分后更快判断志愿填报方向'
      ]
    }
  }

  if (stage === 'wait_score' || stage === 'after_score') {
    return {
      title: '2026 中考已经出分，等待官方发布分段表后即可使用',
      bodySuffix: '先做一次预演。官方分段表发布并录入后，即可切换为 2026 版正式测算。',
      bullets: [
        '先了解五校之间的分数梯度',
        '看清「冲 / 稳 / 保」三档区间',
        '分段表发布后即可正式测算'
      ]
    }
  }

  return {
    title: '2026 中考还未开始',
    bodySuffix: '做一次预演。中考结束、成绩和录取公告发布后，我们会尽快切换为 2026 版正式测算。',
    bullets: [
      '提前感受五校之间的分数梯度',
      '看清「冲 / 稳 / 保」三档区间',
      '真正出分填报时，对算法和数据有概念'
    ]
  }
}
